// ═══════════════════════════════════════════════════════════════════════════════
// SHARED PRICING CONSTANTS - SINGLE SOURCE OF TRUTH
// ═══════════════════════════════════════════════════════════════════════════════
// This file is the master source for all pricing data. 
// Changes here should be synced to: supabase/functions/_shared/pricing-constants.ts
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// FLAT RATE DESTINATIONS - Only Austin remains as flat rate
// ═══════════════════════════════════════════════════════════════════════════════
export interface FlatRateDestination {
  keywords: string[];
  rate: number;
  name: string;
  miles: number;
  minutes: number;
  minDistance?: number;
  maxDistance?: number;
}

// Austin is the ONLY flat rate destination - Updated February 2026
export const FLAT_RATE_DESTINATIONS: FlatRateDestination[] = [
  { keywords: ['austin', 'bergstrom', 'aus'], rate: 474.00, name: 'Austin', miles: 80, minutes: 75, minDistance: 70, maxDistance: 90 },
];

// ═══════════════════════════════════════════════════════════════════════════════
// FORMULA PRICING (≥15 miles, not covered by flat rates)
// ═══════════════════════════════════════════════════════════════════════════════
export const FORMULA_PRICING = {
  BASE_FEE: 50,
  PER_MILE: 3.25,
  PER_MINUTE: 1.25,
  ADDITIONAL_STOP: 25,
  LATE_NIGHT_SURCHARGE: 35, // Late-Night/Early Morning Surcharge: 9 PM - 5 AM
  GRATUITY_RATE: 0.20,
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOCAL SERVICE (<15 miles)
// ═══════════════════════════════════════════════════════════════════════════════
export const LOCAL_SERVICE = {
  PRICE: 132, // Total price including 20% gratuity
  BASE_FARE: 110,
  DISTANCE_THRESHOLD: 15, // miles
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAJOR CITY RATES - Only Austin
// ═══════════════════════════════════════════════════════════════════════════════
export const MAJOR_CITY_RATES: Record<string, { price: number; miles: number; minutes: number; description: string }> = {
  austin: { price: 474, miles: 80, minutes: 75, description: "Austin-Bergstrom Airport (AUS), Downtown Austin, UT Campus" },
};

// ═══════════════════════════════════════════════════════════════════════════════
// SMART DESTINATIONS - Pre-programmed distances for common routes
// ═══════════════════════════════════════════════════════════════════════════════
export interface SmartDestination {
  keywords: string[];
  distance: number;
  duration: number;
}

export const SMART_DESTINATIONS: SmartDestination[] = [
  // Major Cities
  { keywords: ['houston', 'iah', 'hou', 'hobby'], distance: 195.0, duration: 180.0 },
  { keywords: ['austin', 'aus', 'bergstrom'], distance: 80.0, duration: 75.0 },
  { keywords: ['dallas', 'dfw'], distance: 275.0, duration: 260.0 },
  { keywords: ['corpus christi'], distance: 145.0, duration: 140.0 },
  { keywords: ['el paso'], distance: 550.0, duration: 520.0 },
  { keywords: ['laredo'], distance: 155.0, duration: 145.0 },
  // Hill Country & Nearby
  { keywords: ['boerne'], distance: 32.0, duration: 35.0 },
  { keywords: ['new braunfels', 'gruene'], distance: 35.0, duration: 38.0 },
  { keywords: ['canyon lake'], distance: 45.0, duration: 50.0 },
  { keywords: ['fair oaks ranch', 'fair oaks'], distance: 25.0, duration: 28.0 },
  { keywords: ['seguin'], distance: 40.0, duration: 45.0 },
  // Hill Country Extended
  { keywords: ['kerrville'], distance: 65.0, duration: 70.0 },
  { keywords: ['fredericksburg'], distance: 75.0, duration: 80.0 },
  { keywords: ['bandera'], distance: 50.0, duration: 55.0 },
  { keywords: ['uvalde'], distance: 85.0, duration: 85.0 },
  { keywords: ['marble falls'], distance: 95.0, duration: 95.0 },
  { keywords: ['wimberley'], distance: 70.0, duration: 75.0 },
  // Texas Coast
  { keywords: ['port aransas'], distance: 165.0, duration: 160.0 },
  { keywords: ['rockport'], distance: 150.0, duration: 145.0 },
  { keywords: ['south padre island', 'south padre'], distance: 290.0, duration: 275.0 },
  { keywords: ['galveston'], distance: 220.0, duration: 210.0 },
  // Central Texas
  { keywords: ['san marcos'], distance: 50.0, duration: 50.0 },
  { keywords: ['waco'], distance: 165.0, duration: 155.0 },
  { keywords: ['lockhart'], distance: 75.0, duration: 75.0 },
  { keywords: ['victoria'], distance: 120.0, duration: 120.0 },
  // San Antonio Suburbs
  { keywords: ['schertz'], distance: 20.0, duration: 25.0 },
  { keywords: ['helotes'], distance: 18.0, duration: 22.0 },
  { keywords: ['castroville'], distance: 25.0, duration: 30.0 },
  { keywords: ['lakehills'], distance: 38.0, duration: 45.0 },
  // Local San Antonio Areas
  { keywords: ['stone oak'], distance: 17.0, duration: 22.0 },
  { keywords: ['downtown', 'riverwalk'], distance: 9.0, duration: 12.0 },
  { keywords: ['pearl', 'pearl district'], distance: 8.0, duration: 11.0 },
  { keywords: ['alamo heights'], distance: 6.0, duration: 10.0 },
  { keywords: ['la cantera'], distance: 14.0, duration: 18.0 },
  { keywords: ['medical center'], distance: 7.0, duration: 10.0 },
  { keywords: ['sat airport', 'san antonio airport', 'sat'], distance: 10.0, duration: 15.0 },
];

// ═══════════════════════════════════════════════════════════════════════════════
// DISTANCE DATABASE - Normalized keys for programmatic lookup
// ═══════════════════════════════════════════════════════════════════════════════
export const DISTANCE_DATABASE: Record<string, { miles: number; minutes: number }> = {
  // Major Texas Cities
  austin: { miles: 80, minutes: 75 },
  houston: { miles: 195, minutes: 180 },
  dallas: { miles: 275, minutes: 260 },
  corpuschristi: { miles: 145, minutes: 140 },
  elpaso: { miles: 550, minutes: 520 },
  laredo: { miles: 155, minutes: 145 },
  // Hill Country & Nearby
  boerne: { miles: 32, minutes: 35 },
  newbraunfels: { miles: 35, minutes: 38 },
  kerrville: { miles: 65, minutes: 70 },
  fredericksburg: { miles: 75, minutes: 80 },
  bandera: { miles: 50, minutes: 55 },
  uvalde: { miles: 85, minutes: 85 },
  marblefalls: { miles: 95, minutes: 95 },
  wimberley: { miles: 70, minutes: 75 },
  // Texas Coast
  portaransas: { miles: 165, minutes: 160 },
  rockport: { miles: 150, minutes: 145 },
  southpadreisland: { miles: 290, minutes: 275 },
  galveston: { miles: 220, minutes: 210 },
  // Central Texas
  sanmarcos: { miles: 50, minutes: 50 },
  waco: { miles: 165, minutes: 155 },
  lockhart: { miles: 75, minutes: 75 },
  victoria: { miles: 120, minutes: 120 },
  // San Antonio Suburbs
  fairoaksranch: { miles: 25, minutes: 28 },
  canyonlake: { miles: 45, minutes: 50 },
  seguin: { miles: 40, minutes: 45 },
  schertz: { miles: 20, minutes: 25 },
  helotes: { miles: 18, minutes: 22 },
  castroville: { miles: 25, minutes: 30 },
  lakehills: { miles: 38, minutes: 45 },
  // Local San Antonio areas
  downtown: { miles: 9, minutes: 12 },
  pearl: { miles: 8, minutes: 11 },
  alamoheights: { miles: 6, minutes: 10 },
  stoneoak: { miles: 17, minutes: 22 },
  lacantera: { miles: 14, minutes: 18 },
  medicalcenter: { miles: 7, minutes: 10 },
};

// ═══════════════════════════════════════════════════════════════════════════════
// TEXAS BUSINESS DIRECTORY
// Hotels, Airports, Landmarks, Military Bases, Wineries, etc.
// ═══════════════════════════════════════════════════════════════════════════════
export interface BusinessEntry {
  name: string;
  aliases: string[];
  city: string;
  distance: number;
  duration: number;
  category: string;
}

export const BUSINESS_DIRECTORY: BusinessEntry[] = [
  // HOTELS & RESORTS
  { name: "Hotel Emma", aliases: ["emma", "hotel emma", "hotelemma"], city: "San Antonio", distance: 2, duration: 6, category: "hotel" },
  { name: "The St. Anthony Hotel", aliases: ["st anthony", "saint anthony", "stanthony"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "Hotel Contessa", aliases: ["contessa", "hotel contessa"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "Mokara Hotel & Spa", aliases: ["mokara", "mokara hotel", "mokara spa"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "Grand Hyatt San Antonio", aliases: ["grand hyatt", "grandhyatt", "hyatt riverwalk"], city: "San Antonio", distance: 1, duration: 5, category: "hotel" },
  { name: "JW Marriott San Antonio", aliases: ["jw marriott", "jwmarriott", "marriott hill country"], city: "San Antonio", distance: 18, duration: 22, category: "hotel" },
  { name: "La Cantera Resort & Spa", aliases: ["la cantera resort", "lacantera resort", "cantera resort"], city: "San Antonio", distance: 16, duration: 20, category: "hotel" },
  { name: "Eilan Hotel & Spa", aliases: ["eilan", "eilan hotel", "eilan spa"], city: "San Antonio", distance: 17, duration: 21, category: "hotel" },
  { name: "Hyatt Regency San Antonio", aliases: ["hyatt regency", "hyatt riverwalk", "hyatt regency riverwalk"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "Omni La Mansion del Rio", aliases: ["omni la mansion", "la mansion", "omni riverwalk"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "Thompson San Antonio", aliases: ["thompson hotel", "thompson san antonio"], city: "San Antonio", distance: 1, duration: 4, category: "hotel" },
  { name: "The Driskill Hotel", aliases: ["driskill", "the driskill"], city: "Austin", distance: 80, duration: 75, category: "hotel" },
  { name: "Four Seasons Austin", aliases: ["four seasons austin", "4 seasons austin"], city: "Austin", distance: 80, duration: 75, category: "hotel" },
  { name: "The Post Oak Hotel", aliases: ["post oak hotel", "post oak"], city: "Houston", distance: 195, duration: 180, category: "hotel" },
  // HILL COUNTRY B&Bs
  { name: "Ye Kendall Inn", aliases: ["ye kendall", "kendall inn", "ye kendall inn"], city: "Boerne", distance: 32, duration: 35, category: "bnb" },
  { name: "Gruene Mansion Inn", aliases: ["gruene mansion", "gruene mansion inn"], city: "New Braunfels", distance: 35, duration: 40, category: "bnb" },
  { name: "Hoffman Haus", aliases: ["hoffman haus", "hoffmanhaus"], city: "Fredericksburg", distance: 75, duration: 80, category: "bnb" },
  // SHOPPING CENTERS
  { name: "The Shops at La Cantera", aliases: ["la cantera", "lacantera", "cantera mall", "shops at la cantera"], city: "San Antonio", distance: 16, duration: 20, category: "shopping" },
  { name: "The Rim", aliases: ["rim", "the rim", "rim shopping"], city: "San Antonio", distance: 17, duration: 21, category: "shopping" },
  { name: "North Star Mall", aliases: ["north star", "northstar mall", "northstar"], city: "San Antonio", distance: 8, duration: 12, category: "shopping" },
  { name: "The Pearl", aliases: ["pearl", "pearl district", "pearl brewery", "the pearl district"], city: "San Antonio", distance: 2, duration: 6, category: "shopping" },
  { name: "San Marcos Premium Outlets", aliases: ["san marcos outlets", "premium outlets", "outlet mall"], city: "San Marcos", distance: 50, duration: 50, category: "shopping" },
  { name: "The Domain", aliases: ["domain", "the domain austin", "domain austin"], city: "Austin", distance: 80, duration: 75, category: "shopping" },
  { name: "The Galleria Houston", aliases: ["galleria houston", "galleria", "houston galleria"], city: "Houston", distance: 195, duration: 180, category: "shopping" },
  // HOSPITALS & MEDICAL
  { name: "BAMC", aliases: ["bamc", "brook army", "brooke army", "bamc hospital", "brooke army medical"], city: "San Antonio", distance: 8, duration: 15, category: "hospital" },
  { name: "Methodist Hospital", aliases: ["methodist", "methodist hospital"], city: "San Antonio", distance: 7, duration: 12, category: "hospital" },
  { name: "University Hospital", aliases: ["university hospital", "uthsa hospital"], city: "San Antonio", distance: 7, duration: 12, category: "hospital" },
  { name: "MD Anderson Cancer Center", aliases: ["md anderson", "mdanderson", "anderson cancer"], city: "Houston", distance: 195, duration: 180, category: "hospital" },
  { name: "Texas Medical Center", aliases: ["texas medical center", "tmc", "houston medical center"], city: "Houston", distance: 195, duration: 180, category: "hospital" },
  // SPORTS VENUES
  { name: "Alamodome", aliases: ["alamodome", "alamo dome", "the alamodome"], city: "San Antonio", distance: 1, duration: 5, category: "sports" },
  { name: "Frost Bank Center", aliases: ["frost bank center", "att center", "at&t center", "spurs arena"], city: "San Antonio", distance: 5, duration: 10, category: "sports" },
  { name: "Darrell K Royal Stadium", aliases: ["dkr stadium", "darrell royal", "ut stadium"], city: "Austin", distance: 80, duration: 75, category: "sports" },
  { name: "NRG Stadium", aliases: ["nrg stadium", "nrg", "texans stadium"], city: "Houston", distance: 195, duration: 180, category: "sports" },
  // UNIVERSITIES
  { name: "UTSA", aliases: ["utsa", "ut san antonio", "utsa main campus"], city: "San Antonio", distance: 14, duration: 18, category: "university" },
  { name: "Trinity University", aliases: ["trinity", "trinity university"], city: "San Antonio", distance: 4, duration: 8, category: "university" },
  { name: "UT Austin", aliases: ["ut austin", "ut", "texas longhorns", "forty acres"], city: "Austin", distance: 80, duration: 75, category: "university" },
  // AIRPORTS
  { name: "San Antonio International Airport (SAT)", aliases: ["sat", "san antonio airport", "sat airport", "sa airport"], city: "San Antonio", distance: 8, duration: 12, category: "airport" },
  { name: "Austin-Bergstrom Airport (AUS)", aliases: ["aus", "austin airport", "bergstrom", "abia"], city: "Austin", distance: 85, duration: 80, category: "airport" },
  { name: "George Bush Airport (IAH)", aliases: ["iah", "bush airport", "houston iah", "intercontinental"], city: "Houston", distance: 200, duration: 185, category: "airport" },
  { name: "Hobby Airport (HOU)", aliases: ["hou", "hobby", "hobby airport", "houston hobby"], city: "Houston", distance: 190, duration: 175, category: "airport" },
  // LANDMARKS & ATTRACTIONS
  { name: "The Alamo", aliases: ["alamo", "the alamo", "alamo mission"], city: "San Antonio", distance: 1, duration: 4, category: "landmark" },
  { name: "San Antonio River Walk", aliases: ["riverwalk", "river walk", "paseo del rio"], city: "San Antonio", distance: 1, duration: 4, category: "landmark" },
  { name: "SeaWorld San Antonio", aliases: ["seaworld", "sea world", "seaworld sa"], city: "San Antonio", distance: 15, duration: 20, category: "landmark" },
  { name: "Six Flags Fiesta Texas", aliases: ["fiesta texas", "six flags", "six flags san antonio"], city: "San Antonio", distance: 18, duration: 22, category: "landmark" },
  { name: "Natural Bridge Caverns", aliases: ["natural bridge", "natural bridge caverns", "caverns"], city: "San Antonio", distance: 25, duration: 28, category: "landmark" },
  { name: "Schlitterbahn New Braunfels", aliases: ["schlitterbahn", "schlitterbahn nb"], city: "New Braunfels", distance: 35, duration: 38, category: "landmark" },
  { name: "Gruene Historic District", aliases: ["gruene", "gruene hall", "gruene texas"], city: "New Braunfels", distance: 35, duration: 40, category: "landmark" },
  { name: "Enchanted Rock", aliases: ["enchanted rock", "enchanted rock state park"], city: "Fredericksburg", distance: 90, duration: 95, category: "landmark" },
  { name: "Zilker Park", aliases: ["zilker", "zilker park", "barton springs"], city: "Austin", distance: 80, duration: 75, category: "landmark" },
  { name: "Space Center Houston", aliases: ["space center", "nasa houston", "johnson space center"], city: "Houston", distance: 200, duration: 185, category: "landmark" },
  // CORPORATE & CONVENTION CENTERS
  { name: "USAA Headquarters", aliases: ["usaa", "usaa hq", "usaa headquarters"], city: "San Antonio", distance: 10, duration: 15, category: "corporate" },
  { name: "Henry B. Gonzalez Convention Center", aliases: ["convention center", "gonzalez convention center", "sa convention center", "downtown convention center"], city: "San Antonio", distance: 1, duration: 5, category: "corporate" },
  { name: "Tesla Gigafactory Texas", aliases: ["tesla", "tesla austin", "gigafactory"], city: "Austin", distance: 85, duration: 80, category: "corporate" },
  { name: "Austin Convention Center", aliases: ["austin convention center", "austin convention"], city: "Austin", distance: 80, duration: 75, category: "corporate" },
  { name: "George R. Brown Convention Center", aliases: ["grb", "george r brown", "houston convention center"], city: "Houston", distance: 195, duration: 180, category: "corporate" },
  // MILITARY BASES (kept for distance lookup, no flat rates)
  { name: "Lackland AFB", aliases: ["lackland", "lackland afb", "bmt", "bmt graduation", "basic training"], city: "San Antonio", distance: 18, duration: 25, category: "military" },
  { name: "Fort Sam Houston", aliases: ["fort sam", "fort sam houston", "fsh"], city: "San Antonio", distance: 7, duration: 12, category: "military" },
  { name: "Randolph AFB", aliases: ["randolph", "randolph afb", "randolph air force"], city: "Universal City", distance: 20, duration: 28, category: "military" },
  { name: "Camp Bullis", aliases: ["camp bullis", "bullis"], city: "San Antonio", distance: 19, duration: 22, category: "military" },
  // WINERIES (Top Texas Wine Country)
  { name: "Becker Vineyards", aliases: ["becker", "becker vineyards", "becker winery"], city: "Fredericksburg", distance: 75, duration: 80, category: "winery" },
  { name: "Grape Creek Vineyards", aliases: ["grape creek", "grapecreek"], city: "Fredericksburg", distance: 75, duration: 80, category: "winery" },
  { name: "Pedernales Cellars", aliases: ["pedernales", "pedernales cellars"], city: "Stonewall", distance: 70, duration: 75, category: "winery" },
  { name: "William Chris Vineyards", aliases: ["william chris", "william chris vineyards"], city: "Hye", distance: 75, duration: 80, category: "winery" },
  { name: "Duchman Family Winery", aliases: ["duchman", "duchman family"], city: "Driftwood", distance: 60, duration: 65, category: "winery" },
  { name: "Dry Comal Creek Vineyards", aliases: ["dry comal", "dry comal creek"], city: "New Braunfels", distance: 35, duration: 38, category: "winery" },
  // RESTAURANTS
  { name: "Supper at Hotel Emma", aliases: ["supper", "supper hotel emma"], city: "San Antonio", distance: 2, duration: 6, category: "restaurant" },
  { name: "Bohanan's Prime Steaks", aliases: ["bohanans", "bohanan", "bohanans steakhouse"], city: "San Antonio", distance: 1, duration: 4, category: "restaurant" },
  { name: "Franklin Barbecue", aliases: ["franklin", "franklin bbq", "franklin barbecue"], city: "Austin", distance: 80, duration: 75, category: "restaurant" },
  // VENUES
  { name: "Tobin Center", aliases: ["tobin center", "tobin"], city: "San Antonio", distance: 1, duration: 4, category: "venue" },
  { name: "Majestic Theatre", aliases: ["majestic", "majestic theatre", "majestic theater"], city: "San Antonio", distance: 1, duration: 4, category: "venue" },
  { name: "Kendall Point", aliases: ["kendall point", "kendall plantation"], city: "Boerne", distance: 35, duration: 40, category: "venue" },
];

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

export function normalizeSearchTerm(term: string): string {
  return term.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function findBusiness(location: string): BusinessEntry | null {
  const searchTerm = normalizeSearchTerm(location);
  if (!searchTerm || searchTerm.length < 3) return null;
  
  for (const business of BUSINESS_DIRECTORY) {
    // Check exact alias matches first
    for (const alias of business.aliases) {
      const normalizedAlias = normalizeSearchTerm(alias);
      if (searchTerm.includes(normalizedAlias) || normalizedAlias.includes(searchTerm)) {
        return business;
      }
    }
    // Check name match
    const normalizedName = normalizeSearchTerm(business.name);
    if (searchTerm.includes(normalizedName) || normalizedName.includes(searchTerm)) {
      return business;
    }
  }
  
  return null;
}

export function isLateNight(time: string): boolean {
  if (!time) return false;
  const hour = parseInt(time.split(":")[0]);
  return hour >= 21 || hour < 5; // 9 PM - 5 AM
}

export function checkFlatRate(destination: string, distance?: number): { rate: number; name: string; miles: number; minutes: number } | null {
  const destLower = destination.toLowerCase();
  
  for (const flatRate of FLAT_RATE_DESTINATIONS) {
    const matchesKeyword = flatRate.keywords.some(kw => destLower.includes(kw));
    if (matchesKeyword) {
      // Check distance constraints if they exist
      if (flatRate.minDistance && flatRate.maxDistance && distance !== undefined) {
        if (distance >= flatRate.minDistance && distance <= flatRate.maxDistance) {
          return { rate: flatRate.rate, name: flatRate.name, miles: flatRate.miles, minutes: flatRate.minutes };
        }
      } else if (!flatRate.minDistance && !flatRate.maxDistance) {
        return { rate: flatRate.rate, name: flatRate.name, miles: flatRate.miles, minutes: flatRate.minutes };
      }
    }
  }
  return null;
}

export function calculateFormulaPrice(
  miles: number, 
  minutes: number, 
  isLateNightPickup: boolean, 
  additionalStops: number = 0
): {
  total: number;
  breakdown: {
    baseFee: number;
    mileageCharge: number;
    timeCharge: number;
    additionalStopsCharge: number;
    lateNightSurcharge: number;
    subtotal: number;
    gratuity: number;
  };
} {
  const baseFee = FORMULA_PRICING.BASE_FEE;
  const mileageCharge = miles * FORMULA_PRICING.PER_MILE;
  const timeCharge = minutes * FORMULA_PRICING.PER_MINUTE;
  const additionalStopsCharge = additionalStops * FORMULA_PRICING.ADDITIONAL_STOP;
  const lateNightSurcharge = isLateNightPickup ? FORMULA_PRICING.LATE_NIGHT_SURCHARGE : 0;
  
  const subtotal = baseFee + mileageCharge + timeCharge + additionalStopsCharge + lateNightSurcharge;
  const gratuity = subtotal * FORMULA_PRICING.GRATUITY_RATE;
  const total = Math.round((subtotal + gratuity) * 100) / 100;
  
  return {
    total,
    breakdown: {
      baseFee,
      mileageCharge: Math.round(mileageCharge * 100) / 100,
      timeCharge: Math.round(timeCharge * 100) / 100,
      additionalStopsCharge,
      lateNightSurcharge,
      subtotal: Math.round(subtotal * 100) / 100,
      gratuity: Math.round(gratuity * 100) / 100,
    },
  };
}
